import { useState } from "react"

import { Dialog } from "@minuthq/meatball-ui-react"
import { useFormik } from "formik"

import { TaskTemplateForm } from "src/components/TaskManagement/templates/TaskTemplateForm"
import { useOrganization } from "src/data/organizations/hooks/useOrganization"
import {
  TTaskTemplate,
  TTaskTemplatePostBody,
} from "src/data/taskManagement/taskManagementTypes"
import { usePatchTaskTemplate } from "src/data/taskManagement/taskTemplateQueries"
import { useTranslate } from "src/i18n/useTranslate"
import { DiscardChangesDialog } from "src/ui/Dialog/DiscardChangesDialog"

const EDIT_TEMPLATE_FORM_ID = "edit_template_form"

export function EditTaskTemplateDialog({
  template,
  onClose,
}: {
  template: TTaskTemplate
  onClose: () => void
}) {
  const { t, langKeys } = useTranslate()
  const { orgId } = useOrganization()

  const [showConfirmDiscardDialog, setShowConfirmDiscardDialog] =
    useState(false)

  const patchTaskTemplate = usePatchTaskTemplate()

  const form = useFormik<TTaskTemplatePostBody>({
    initialValues: {
      title: template.title,
      description: template.description,
      task_title: template.task_title,
      task_description: template.task_description,
      task_category: template.task_category,
    },
    onSubmit: (values) => {
      patchTaskTemplate.mutate(
        {
          path: {
            organization_id: orgId,
            task_template_id: template.template_id,
          },
          body: { ...values },
        },
        {
          onSuccess: () => {
            onClose()
          },
        }
      )
    },
  })

  function handleDiscardConfirmed() {
    setShowConfirmDiscardDialog(false)
    onClose()
  }

  function handleClose() {
    if (form.dirty) {
      setShowConfirmDiscardDialog(true)
    } else {
      onClose()
    }
  }

  return (
    <Dialog
      open={true}
      onClose={handleClose}
      title={t(langKeys.task_management_edit_task_template)}
      confirmLabel={t(langKeys.save)}
      formId={EDIT_TEMPLATE_FORM_ID}
      closeLabel={t(langKeys.cancel)}
      confirmButtonProps={{
        loading: patchTaskTemplate.isLoading,
      }}
      error={
        patchTaskTemplate.isError ? t(langKeys.failed_something_went_wrong) : ""
      }
    >
      {showConfirmDiscardDialog && (
        <DiscardChangesDialog
          open={showConfirmDiscardDialog}
          onClose={() => {
            setShowConfirmDiscardDialog(false)
          }}
          onConfirm={handleDiscardConfirmed}
        />
      )}

      <form id={EDIT_TEMPLATE_FORM_ID} onSubmit={form.handleSubmit}>
        <TaskTemplateForm form={form} />
      </form>
    </Dialog>
  )
}
